import { t } from 'i18next';
import { useEffect, useState } from 'react';

import { CommonFlex, CommonText } from '../../styledComponents/common';
import NoDataComponent from '../NoDataComponent/NoDataComponent';
import ChartTooltip from '../Tooltip/ChartTooltip';
import {
  AbsoluteScaleContainer,
  Bar,
  CenteredText,
  HorizontalScale,
  LegendColor,
  LegendContainer,
  LegendItem,
  LegendText,
  ScrollableContainer,
  VerticalLabel,
  VerticalScale,
} from './BarChartStyles';

const BarChartSingle = ({
  data = [],
  isLoading,
  xAxisLabel,
  maxValue = 100,
}) => {
  const [max, setMax] = useState(maxValue);
  const [tooltipContent, setTooltipContent] = useState('');
  const [tooltipPosition, setTooltipPosition] = useState(null);

  // useEffect(() => {
  //   if (data && Array.isArray(data) && data.length > 0) {
  //     // Find the maximum avg_of_evaluation value
  //     let maxValue = 0;

  //     data.forEach(item => {
  //       maxValue = Math.max(maxValue, item.average_total_score || 0);
  //     });

  //     // Round up to the nearest 10
  //     setMax(Math.ceil(maxValue / 10) * 10);
  //   }
  // }, [data, maxValue]);

  const getBarColor = value => {
    if (value < 40) return '#d80101'; // Red
    if (value < 70) return '#E97132'; // Orange
    return '#196B24'; // Green
  };

  const handleMouseEnter = (event, item) => {
    const rect = event.target.getBoundingClientRect();
    const text = `${t('avg_of_evaluation')}: ${item.average_total_score || 0} %`;

    setTooltipContent(text);
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
    });
  };

  const handleMouseLeave = () => {
    setTooltipPosition(null);
  };

  // Legend configuration based on value ranges
  const legendConfig = [
    { value: '< 40%', color: '#D83B01', label: t('low_performance') },
    { value: '< 70%', color: '#E97132', label: t('medium_performance') },
    { value: '≥ 70%', color: '#196B24', label: t('high_performance') },
  ];

  // Check if data is empty or undefined
  const hasData = data && Array.isArray(data) && data.length > 0;

  if (!hasData) {
    return (
      <NoDataComponent
        title={t('no_evaluation_data_found')}
        padding="5vh 0"
        imageHeight="140px"
      />
    );
  }

  return (
    <>
      <ChartTooltip
        content={<div dangerouslySetInnerHTML={{ __html: tooltipContent }} />}
        position={tooltipPosition}
      />

      <LegendContainer>
        {legendConfig.map(item => (
          <LegendItem key={item.value}>
            <LegendColor color={item.color} />
            <LegendText>
              {item.label} ({item.value})
            </LegendText>
          </LegendItem>
        ))}
      </LegendContainer>
      <div
        style={{
          position: 'relative',
          height: 0,
          width: '100%',
          margin: 0,
        }}
      >
        <VerticalLabel>{t('avg_of_evaluation')}</VerticalLabel>
      </div>
      <ScrollableContainer>
        <CommonFlex
          gap="20px"
          alignItems="flex-start"
          justifyContent="start"
          style={{ height: '100%' }}
        >
          <CommonFlex direction="column" gap="20px" alignItems="flex-start">
            {Array.from({ length: 11 }, (_, index) => {
              const v = (10 - index) * (100 / 10);
              return (
                <VerticalScale
                  style={{
                    width: '40px',
                  }}
                  key={v}
                >
                  {Math.round(v)}%
                </VerticalScale>
              );
            })}
          </CommonFlex>
          <CommonFlex
            style={{
              height: 'calc(100% - 24px)',
              marginTop: '11px',
              zIndex: 2,
              position: 'relative',
              width: '100%',
              padding: '0',
            }}
            gap="30px"
            justifyContent="flex-start"
          >
            {data.map((item, index) => (
              <CommonFlex
                direction="column"
                alignItems="bottom"
                justifyContent="flex-end"
                key={`bar-${index}-${item.id || index}`}
                style={{ height: '100%', position: 'relative' }}
              >
                <CommonText
                  name
                  title
                  fontSize="12px"
                  style={{
                    position: 'absolute',
                    bottom: '-20px',
                    width: '100%',
                    textAlign: 'center',
                  }}
                >
                  {item.office_name || `Office ${index + 1}`}
                </CommonText>
                <CommonFlex
                  direction="column"
                  gap="0"
                  style={{ height: '100%', cursor: 'pointer' }}
                  alignItems="bottom"
                  justifyContent="flex-end"
                  onMouseEnter={e => handleMouseEnter(e, item)}
                  onMouseLeave={handleMouseLeave}
                >
                  <Bar
                    bgColor={getBarColor(item.average_total_score)}
                    height={`${(100 * (item.average_total_score || 0)) / max}%`}
                    onMouseEnter={e => handleMouseEnter(e, item)}
                    onMouseLeave={handleMouseLeave}
                  />
                </CommonFlex>
              </CommonFlex>
            ))}
          </CommonFlex>
        </CommonFlex>
        <AbsoluteScaleContainer
          style={{
            left: '42px',
          }}
          direction="column"
          gap="20px"
        >
          {Array.from({ length: 11 }, (_, index) => {
            const v = (10 - index) * (max / 10);
            return (
              <HorizontalScale
                key={v}
                width={
                  data?.length > 0
                    ? `${Math.max(60 * data.length, 700)}px`
                    : '100%'
                }
              />
            );
          })}
        </AbsoluteScaleContainer>
      </ScrollableContainer>
      <CenteredText>{xAxisLabel || t('offices')}</CenteredText>
    </>
  );
};

export default BarChartSingle;
